import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Link,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  FolderOpen as FolderIcon,
  Unarchive as UnarchiveIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useQuery, useQueryClient } from 'react-query';
import api from '../../utils/api';
import { useIsOffline, OfflineNotice, skipWhenOffline } from '../../offline';
import { useRBAC } from '../../auth/useRBAC';
import { patientChartControls } from '../../auth/pageControls';
import { downloadAuthedBlob } from '../../hooks/useAuthedBlobUrl';
import DocumentViewerModal from '../DocumentViewerModal';
import { usePatientFiles, patientFilesQueryKey } from './usePatientFiles';
import { clinicalArchiveService } from '../../services/clinicalArchiveService';
import {
  buildFileRows,
  countBySource,
  formatFileSize,
  formatFileDate,
  SOURCE_FILTERS,
  FileSource,
} from './patientFiles';

const UPLOAD_ACCEPT = '.pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.txt';

const getFileIcon = (mimeType?: string | null) => {
  if (mimeType?.startsWith('image/')) return <ImageIcon fontSize="small" />;
  if (mimeType === 'application/pdf') return <PdfIcon fontSize="small" />;
  return <FileIcon fontSize="small" />;
};

const SOURCE_CHIP_COLOR: Record<FileSource, string> = {
  soap: '#1565C0',
  lab: '#EF6C00',
  photo: '#6A1B9A',
  document: '#455A64',
};

interface PatientFilesTabProps {
  horseId: string;
  /** Opens the given SOAP note (full object) in the chart's SOAP dialog. When
   *  provided, a file's "Belongs to" SOAP label becomes a clickable link to the
   *  exact note — disambiguating multiple notes on the same day. */
  onOpenSoapNote?: (note: any) => void;
}

interface ViewerState {
  open: boolean;
  url: string;
  title: string;
  mimeType?: string | null;
}

/**
 * Shared "Files" surface for the patient record. Mounted in both chart layouts
 * (HorseProfileClassic tab + HorseProfileModern swap-panel). Aggregates every
 * attachment for the horse, labels each with its source + the record it belongs
 * to, and supports view / download / delete plus a general document upload.
 *
 * Online-only: attachment bytes are never replicated offline, so the whole tab
 * gates to an offline notice when connectivity is observed down.
 */
const PatientFilesTab: React.FC<PatientFilesTabProps> = ({ horseId, onOpenSoapNote }) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const isOffline = useIsOffline();
  const rbac = useRBAC();
  // The chart map, so the tab stops spelling a slug inline (the shape
  // `pageControls.wiring.test.ts` scans for) and so its two controls sit beside every other
  // chart gate. POST /attachments/upload is manage:medical_records (:39); DELETE
  // /attachments/:id is manage:medical_records_archive ALONE (:161) — two different
  // capabilities on one tab, which is why they are two flags.
  const can = patientChartControls(rbac);
  // DELETE /attachments/:id requires `manage:medical_records_archive` since the P3
  // sweep re-pointed it off `manage:all` (attachments.controller.ts); mirror it so
  // we never show a button that would 403. This WIDENS the affordance from OWNER
  // alone to every holder of the slug — OWNER, ADMIN, VETERINARIAN and
  // VETERINARY_TECHNICIAN, and not a CSR — deliberately, and strictly in step with
  // the endpoint: the mirror may never move first (that shows three roles a button
  // that 403s) and may never lag (that hides a capability the API grants).
  const canDelete = can.removeFile;

  const { attachments, soapNotes, isLoading } = usePatientFiles(horseId, {
    withSoapIndex: true,
  });

  const [sourceFilter, setSourceFilter] = useState<'all' | FileSource>('all');
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [viewer, setViewer] = useState<ViewerState | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  // A file on a completed SOAP note is archived, not deleted (2026-10); these load only when asked for.
  const { data: archivedFiles = [] } = useQuery<any[]>(
    [...patientFilesQueryKey(horseId), 'archived'],
    () => clinicalArchiveService.listArchived<any>('attachments', horseId!),
    { enabled: !!horseId && showArchived && !isOffline },
  );
  const inputRef = useRef<HTMLInputElement>(null);
  // Track the active viewer blob URL so we can revoke it on close.
  const viewerUrlRef = useRef<string | null>(null);

  // Revoke any open viewer blob URL if the tab unmounts mid-preview.
  useEffect(
    () => () => {
      if (viewerUrlRef.current) URL.revokeObjectURL(viewerUrlRef.current);
    },
    [],
  );

  const rows = useMemo(
    () => buildFileRows(attachments, soapNotes),
    [attachments, soapNotes],
  );
  // Full SOAP-note objects keyed by id, so a "Belongs to" link can open the
  // exact note (SoapNoteDialog restores from the full note, not just an id).
  const soapNoteById = useMemo(() => {
    const m = new Map<string, any>();
    (soapNotes as any[]).forEach((n) => { if (n?.id) m.set(n.id, n); });
    return m;
  }, [soapNotes]);
  const counts = useMemo(() => countBySource(rows), [rows]);
  const visibleRows = useMemo(
    () => (sourceFilter === 'all' ? rows : rows.filter((r) => r.source === sourceFilter)),
    [rows, sourceFilter],
  );

  const handleUploadClick = () => {
    if (isOffline) {
      enqueueSnackbar('Uploading files requires a connection', { variant: 'info' });
      return;
    }
    inputRef.current?.click();
  };

  const handleFilesSelected = async (fileList: FileList | null) => {
    if (!fileList || !fileList.length) return;
    if (!skipWhenOffline()) {
      enqueueSnackbar('Uploading files requires a connection', { variant: 'info' });
      return;
    }
    setUploading(true);
    let uploaded = 0;
    for (const file of Array.from(fileList)) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('horseId', horseId);
        formData.append('category', 'document');
        await api.post('/attachments/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        uploaded += 1;
      } catch {
        enqueueSnackbar(`Failed to upload ${file.name}`, { variant: 'error' });
      }
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
    if (uploaded) {
      await queryClient.invalidateQueries(patientFilesQueryKey(horseId));
      enqueueSnackbar(`${uploaded} file(s) uploaded`, { variant: 'success' });
    }
  };

  const closeViewer = () => {
    if (viewerUrlRef.current) {
      URL.revokeObjectURL(viewerUrlRef.current);
      viewerUrlRef.current = null;
    }
    setViewer(null);
  };

  const handleView = async (id: string, fileName: string, mimeType?: string | null) => {
    if (!skipWhenOffline()) {
      enqueueSnackbar('Viewing files requires a connection', { variant: 'info' });
      return;
    }
    try {
      const res = await api.get(`/attachments/${id}/download`, { responseType: 'blob' });
      if (viewerUrlRef.current) URL.revokeObjectURL(viewerUrlRef.current);
      const blobUrl = URL.createObjectURL(res.data);
      viewerUrlRef.current = blobUrl;
      setViewer({ open: true, url: blobUrl, title: fileName, mimeType });
    } catch {
      enqueueSnackbar('Failed to open file', { variant: 'error' });
    }
  };

  const handleDownload = async (id: string, fileName: string) => {
    if (!skipWhenOffline()) {
      enqueueSnackbar('Downloading files requires a connection', { variant: 'info' });
      return;
    }
    try {
      await downloadAuthedBlob(`/attachments/${id}/download`, fileName);
    } catch {
      enqueueSnackbar('Failed to download file', { variant: 'error' });
    }
  };

  const handleRestore = async (id: string) => {
    try {
      await clinicalArchiveService.restore('attachments', id);
      await queryClient.invalidateQueries(patientFilesQueryKey(horseId));
      enqueueSnackbar('File restored', { variant: 'success' });
    } catch {
      enqueueSnackbar('Failed to restore file', { variant: 'error' });
    }
  };

  const handleDelete = async (id: string, fileName: string) => {
    if (!skipWhenOffline()) {
      enqueueSnackbar('Removing files requires a connection', { variant: 'info' });
      return;
    }
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove "${fileName}"? A file on a completed SOAP note is archived and can be restored; any other file is permanently deleted.`)) return;
    setDeletingId(id);
    try {
      const res = await api.delete(`/attachments/${id}`);
      await queryClient.invalidateQueries(patientFilesQueryKey(horseId));
      enqueueSnackbar(res.data?.archived ? 'File archived' : 'File removed', { variant: 'success' });
    } catch {
      enqueueSnackbar('Failed to remove file', { variant: 'error' });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }} data-testid="patient-files-tab">
      {/* Header: title + filter chips + upload */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          mb: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', flexGrow: 1 }}>
          {SOURCE_FILTERS.map((f) => {
            const active = sourceFilter === f.key;
            const count = counts[f.key];
            return (
              <Chip
                key={f.key}
                label={`${f.label} ${count}`}
                size="small"
                variant={active ? 'filled' : 'outlined'}
                color={active ? 'primary' : 'default'}
                onClick={() => setSourceFilter(f.key)}
                data-testid={`files-filter-${f.key}`}
                sx={{ cursor: 'pointer', fontWeight: active ? 700 : 500 }}
              />
            );
          })}
        </Box>
        {/* Hidden without the slug; still DISABLED when the only problem is the network — the
            two states are different sentences and only one of them has a cure here. */}
        {can.uploadFile && (
          <Button
            variant="contained"
            size="small"
            startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
            onClick={handleUploadClick}
            disabled={uploading || isOffline}
            data-testid="files-upload-button"
          >
            {uploading ? 'Uploading…' : 'Upload file'}
          </Button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={UPLOAD_ACCEPT}
          multiple
          style={{ display: 'none' }}
          onChange={(e) => handleFilesSelected(e.target.files)}
        />
      </Box>

      {isOffline ? (
        <OfflineNotice>
          Files require a connection — reconnect to view, upload, or manage attachments.
        </OfflineNotice>
      ) : isLoading ? (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <CircularProgress size={28} />
        </Box>
      ) : visibleRows.length === 0 ? (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <FolderIcon sx={{ fontSize: 44, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            {rows.length === 0
              ? 'No files uploaded for this patient yet.'
              : 'No files match this filter.'}
          </Typography>
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            overflowX: 'auto',
            boxShadow: 'none',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 3,
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 700 }}>File</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Source</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>When</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Belongs to</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  Size
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((row) => (
                <TableRow key={row.id} hover data-testid="files-row">
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                      <Box sx={{ color: 'text.secondary', display: 'flex' }}>
                        {getFileIcon(row.mimeType)}
                      </Box>
                      <Typography
                        variant="body2"
                        noWrap
                        title={row.fileName}
                        sx={{ fontWeight: 500, maxWidth: 280 }}
                      >
                        {row.fileName}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={row.sourceLabel}
                      size="small"
                      variant="outlined"
                      sx={{
                        height: 22,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        borderColor: SOURCE_CHIP_COLOR[row.source],
                        color: SOURCE_CHIP_COLOR[row.source],
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatFileDate(row.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {row.soapNoteId && onOpenSoapNote ? (
                      <Link
                        component="button"
                        type="button"
                        underline="hover"
                        onClick={() => {
                          const note = soapNoteById.get(row.soapNoteId!);
                          if (note) onOpenSoapNote(note);
                        }}
                        title="Open this SOAP note"
                        sx={{ textAlign: 'left', fontSize: '0.875rem', fontWeight: 500 }}
                      >
                        {row.belongsTo}
                      </Link>
                    ) : (
                      <Typography variant="body2" color={row.belongsTo ? 'text.primary' : 'text.disabled'}>
                        {row.belongsTo || '—'}
                      </Typography>
                    )}
                    {row.belongsToSub && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {row.belongsToSub}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="text.secondary">
                      {formatFileSize(row.fileSize)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.25 }}>
                      <Tooltip title="View">
                        <IconButton
                          size="small"
                          onClick={() => handleView(row.id, row.fileName, row.mimeType)}
                          sx={{ color: 'text.secondary' }}
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download">
                        <IconButton
                          size="small"
                          onClick={() => handleDownload(row.id, row.fileName)}
                          sx={{ color: 'text.secondary' }}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {canDelete && (
                        <Tooltip title="Remove">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(row.id, row.fileName)}
                              disabled={deletingId === row.id}
                              sx={{ color: 'error.main' }}
                            >
                              {deletingId === row.id ? (
                                <CircularProgress size={16} />
                              ) : (
                                <DeleteIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!isOffline && (
        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={<Switch size="small" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />}
            label="Show archived files"
          />
          {showArchived && archivedFiles.length === 0 && (
            <Typography variant="body2" color="text.secondary">No archived files</Typography>
          )}
          {showArchived && archivedFiles.map((f: any) => (
            <Box key={f.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
              <Typography variant="body2" sx={{ flex: 1 }}>{f.fileName}</Typography>
              <Chip label="Archived" size="small" />
              {canDelete && (
                <Button size="small" startIcon={<UnarchiveIcon />} onClick={() => handleRestore(f.id)}>
                  Restore
                </Button>
              )}
            </Box>
          ))}
        </Box>
      )}

      {viewer?.open && (
        <DocumentViewerModal
          open={viewer.open}
          onClose={closeViewer}
          url={viewer.url}
          title={viewer.title}
          mimeType={viewer.mimeType || undefined}
        />
      )}
    </Box>
  );
};

export default PatientFilesTab;
